import React, { useState, useEffect } from 'react';
import { CONTENT_KIT_STATES } from './PublishMenus';
import { Menu } from './PublishV2';
import { makeRandomId } from '../../../helper';
import Masonry from 'react-masonry-css';
import './MenuStyles.css';
import { useSelector } from 'react-redux';
import { isZyncStaff } from '../../../helper/isZyncStaff';
import { Modal, ModalWindow } from '../../../components/Modal';
import { useAgencyDetails } from '../../../hooks/useAgencyDetails';
import { TextInputRaw } from '../../../components/Input/TextInputRaw';
import { Button } from '../../../components/common/Button';
import { notifyUser } from '../../../components/authoring/hooks';
import { saveEmailForContentKitNotify } from '../../../helper/api';

export const ProcessingContentKitView = ({
  menus,
  meetingSeriesId,
  notifyEmail = [],
  useNotifyModal,
}) => {
  const [localNotifyEmail, setLocalNotifyEmail] = useState(notifyEmail);
  const userId = useSelector((st) => st.auth?.user?.userId);
  const [isDismissed, setIsDismissed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { orgLogoUrl, organizationName } = useAgencyDetails();
  const [email, setEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [showNotifyModal, setShowNotifyModal] = useState(false);

  useEffect(() => {
    if (useNotifyModal) {
      const timer = setTimeout(() => {
        setShowNotifyModal(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [useNotifyModal]);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateEmail(email)) {
      setIsEmailValid(true);
      setIsSubmitting(true);
      try {
        await saveEmailForContentKitNotify(meetingSeriesId, email);
        setLocalNotifyEmail((prev) => [...prev, email]);
      } catch (error) {
        notifyUser(
          'We had problem updating your email address. Please try again.'
        );
      } finally {
        notifyUser(`Thank you! We will send the Content Kit link to ${email}`);
        setIsSubmitting(false);
      }
    } else {
      setIsEmailValid(false);
    }
  };

  return (
    <>
      {useNotifyModal ? (
        !showNotifyModal ||
        localNotifyEmail.length > 0 ||
        isDismissed ? null : (
          <ModalWindow
            showCancel
            bg="#fff"
            boxShadow="3xl"
            onCancel={() => setIsDismissed(true)}
            size={Modal.size.md}
          >
            <img
              alt={`${organizationName} logo`}
              className="w-[142px] absolute -top-[120px] left-1/2 -translate-x-1/2"
              src={orgLogoUrl}
            />
            <div className="p-16">
              <h1 className="text-[24px] mb-8 text-blue-dark font-semibold text-center">
                Generating Content Kit
              </h1>
              <p className="text-lg text-center mb-8">
                Drop your email and we'll let you know as soon as it's ready to
                view.
              </p>
              <form
                className="flex flex-col justify-center items-center gap-4"
                onSubmit={handleSubmit}
              >
                <div className="w-full">
                  <TextInputRaw
                    className="w-full"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                  />
                  {!isEmailValid && email !== '' && (
                    <p className="text-red text-xs mt-1">
                      Invalid email address
                    </p>
                  )}
                </div>
                <Button
                  color={Button.colors.PURPLE}
                  padding={Button.padding.MEDIUM}
                  state={
                    isSubmitting ? Button.states.LOADING : Button.states.DEFAULT
                  }
                  type="submit"
                >
                  <span className="text-sm font-medium px-8">Submit</span>
                </Button>
              </form>
            </div>
          </ModalWindow>
        )
      ) : null}
      <div className="flex flex-col justify-center items-center py-[60px]">
        <div className="text-blue-dark text-2xl font-semibold text-center mb-4">
          Your content kit is getting ready...
        </div>
        <div className="text-blue-gray text-sm font-medium text-center mb-4">
          Your fully edited video, webpage, social posts, thumbnails, and more
          are just minutes away!
        </div>
        <div className="flex gap-1 mb-10">
          <span className="text-light-gray text-xs">
            This typically takes 10-15 min.
          </span>
          <button
            className="text-purple decoration-purple underline text-xs"
            onClick={() => {
              window.location.reload();
            }}
          >
            Refresh Now
          </button>
        </div>
        <Masonry
          breakpointCols={2}
          className="masonry-grid"
          columnClassName="masonry-grid_column"
        >
          {menus.map((m) => (
            <Menu
              menu={m}
              key={`menu-${makeRandomId(6)}`}
              contentKitState={CONTENT_KIT_STATES.processing}
            />
          ))}
        </Masonry>
        {isZyncStaff(userId || '') && (
          <a
            href={`https://contentkit.zync.ai/api/processor/statustext?meetingSeriesId=${meetingSeriesId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="cursor-pointer"
          >
            Content kit status
          </a>
        )}
      </div>
    </>
  );
};
